/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Component,
  Directive,
  DoCheck,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { WaitingContext, WaitingMode } from '@gh/core-ui';
import { merge$ } from '@gh/rx';
import { filter$, map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { Subscription } from 'rxjs';
import { BreakpointManager } from '../../services';

import { DelegatedRootLayout, RootLayout, SidebarOptions } from './root-layout';
import { RootToolbarComponent } from './root-toolbar.component';
import { ToolbarOptions } from './toolbar-options';

@Directive({
  standalone: false,
  selector: 'gh-app-bar-part',
})
export class RootLayoutAppBarPartDirective {
}

@Directive({
  standalone: false,
  selector: 'gh-content-part',
})
export class RootLayoutContentPartDirective {
}

@Component({
  standalone: false,
  selector: 'gh-root-layout',
  templateUrl: 'root-layout.component.html',
  styleUrls: ['root-layout.component.scss'],
})
export class RootLayoutComponent implements RootLayout, OnInit, OnDestroy, DoCheck {
  navigationCollapsed: boolean = false;
  navigationOpened: boolean = false;

  backgroundWaiting = false;
  blockedWaiting = false;

  options?: ToolbarOptions;

  sidebarOptions?: SidebarOptions;
  sidebarVisible = false;

  isSmallScreen = false;
  isMediumScreen = false;

  @ViewChild('main', { read: ElementRef, static: true }) mainEl: ElementRef;
  @ViewChild(RootToolbarComponent, { static: true }) toolbar: RootToolbarComponent;

  private mediaSubscription: Subscription;
  private routerSubscription: Subscription;

  constructor(
    private router: Router,
    private breakpoints: BreakpointManager,
    private waitingContext: WaitingContext,
    private root: DelegatedRootLayout,
  ) {
    this.root.setup(this);

    this.onMainContentTouchStart = this.onMainContentTouchStart.bind(this);
  }

  /**
   * Can navigation be fixed as collapsed or expand (only for large screens)
   */
  get isNavigationFixable(): boolean {
    return !this.isMediumScreen && !this.isSmallScreen;
  }

  /**
   * Navigation can be closed if it was collapsed (only for fixable navigation) or if current screen is small
   */
  get isNavigationClosable(): boolean {
    return this.navigationCollapsed || this.isMediumScreen || this.isSmallScreen;
  }

  get isNavigationClosed(): boolean {
    return this.isNavigationClosable && !this.navigationOpened;
  }

  ngOnInit(): void {
    this.mediaSubscription = merge$(
      this.breakpoints.observeMatches('md').pipe(map$((matches) => ({ isMediumScreen: matches }))),
      this.breakpoints.observeMatches('lt-md').pipe(map$((matches) => ({ isSmallScreen: matches }))))
      .subscribe((matches) => {
        assign(this, matches);
      });

    this.routerSubscription = this.router.events.pipe(
      filter$((event) => event instanceof NavigationStart))
      .subscribe(() => this.closeNavigation());

    this.mainEl.nativeElement.addEventListener('touchstart', this.onMainContentTouchStart, { passive: true });
  }

  ngOnDestroy(): void {
    this.mainEl.nativeElement.removeEventListener('touchstart', this.onMainContentTouchStart, { passive: true });

    if (this.mediaSubscription) {
      this.mediaSubscription.unsubscribe();
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  ngDoCheck(): void {
    const { mode, waiting } = this.waitingContext;

    this.blockedWaiting = mode !== WaitingMode.Background && waiting;
    this.backgroundWaiting = mode === WaitingMode.Background && waiting;
  }

  toggleNavigation(open?: boolean): void {
    if (typeof open === 'undefined') {
      open = !this.navigationCollapsed;
    }

    this.navigationCollapsed = open;
    this.navigationOpened = false;
  }

  openCollapsableNavigation(): void {
    if (!this.isSmallScreen) {
      this.openNavigation();
    }
  }

  closeCollapsableNavigation(): void {
    if (!this.isSmallScreen) {
      this.closeNavigation();
    }
  }

  openNavigation(): void {
    if (this.isNavigationClosable) {
      this.navigationOpened = true;
    }
  }

  closeNavigation(): void {
    if (this.isNavigationClosable) {
      this.navigationOpened = false;
    }
  }

  onSidebarClose(): void {
    this.sidebarVisible = false;
  }

  setToolbarOptions(options: ToolbarOptions): void {
    this.options = options;
  }

  resetToolbarOptions(): void {
    this.options = void 0;
  }

  showSidebar(options: SidebarOptions): void {
    this.sidebarVisible = true;
    this.sidebarOptions = options;
  }

  hideSidebar(): void {
    this.sidebarVisible = false;
    this.sidebarOptions = void 0;
  }

  private onMainContentTouchStart(): void {
    this.closeNavigation();
  }
}
